// src/services/Recruit/pages/offerLetterCreate/index.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Stack,
  Flex,
  Text,
  Input,
  FormLabel,
  Select,
  Button,
  Textarea,
  IconButton,
  HStack,
  VStack,
  Divider,
  Badge,
  Circle,
  Image,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import Arrow from "../../assets/img/Arrow.png"
import { ArrowBackIcon, InfoIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { globalStyles } from 'common/theme/styles';
import CustomDatePicker from 'common/components/datePicker/CustomDatePicker';
import TemplateSelector from 'services/Recruit/components/OfferLetter/TemplateSelector';
import SalaryStructure from 'services/Recruit/components/OfferLetter/SalaryStructure';
import OfferContentEditor from 'services/Recruit/components/OfferLetter/OfferContentEditor';
import { getApi } from 'common/services/api';
import { currencyOptions } from 'services/Recruit/data/panel.Data';
import { salaryFrequencyOptions } from 'services/Recruit/data/panel.Data';
import offerLetterService from 'services/Recruit/services/offerLetter';
import '../../assets/css/offerLetter/style.css'
import { constant } from 'common/constant';
import { recruitConstant } from 'services/Recruit/constant';
import { HasAccess } from 'common/utils/accessUtils';
import { accessModuleTitles } from 'common/constant';
import { offerValidationSchema } from 'services/Recruit/schema/offer';
import { isFormDirty } from 'common/utils/formUtils';
import { OFFER_INITIAL_VALUES } from 'common/initialValues/formInitialValues';
import { candidateTabs } from 'services/Recruit/data/candidate';
import { useSelector } from 'react-redux';
import { offerTabs } from 'services/Recruit/data/candidate';
import colors from 'common/theme/foundations/colors';

// Render stepper UI element
const Step = ({ index, isCurrentTab, title, subtitle, tabState, onClick }) => {
  // Show tick for all valid tabs
  const showTick = tabState.isValid;
  
  // Show blue/enabled styling if accessible (past, current, or reachable future)
  const showEnabledStyle = tabState.isAccessible;
  
  // Circle background: blue only if showing tick
  const circleBg = showTick ? '#495AFF' : '#fff';
  
  // Circle border: blue if enabled/accessible, gray if disabled
  const circleBorder = showEnabledStyle ? '#495AFF' : '#ABB7C2';
  
  // Circle text color: white if showing tick, blue if enabled, gray if disabled
  const circleColor = showTick ? '#fff' : showEnabledStyle ? '#495AFF' : '#ABB7C2';
  
  // Title color: blue if enabled, gray if disabled
  const titleColor = showEnabledStyle ? '#495AFF' : '#ABB7C2';

  return (
    <Flex 
      gap={4} 
      onClick={tabState.isAccessible ? onClick : undefined}
      _hover={{
        cursor: tabState.isAccessible ? "pointer" : "not-allowed",
      }}
      _focus={{ boxShadow: "none" }}
      display="flex"
      alignItems="center"
      opacity={tabState.isAccessible ? 1 : 0.6}
      cursor={tabState.isAccessible ? "pointer" : "not-allowed"}
    >
      <Circle 
        size={'22px'} 
        fontSize={'14px'} 
        bg={circleBg} 
        border={`1px solid ${circleBorder}`} 
        color={circleColor} 
        fontWeight="bold"
      >
        {showTick ? '✓' : index + 1}
      </Circle>

      <Box>
        <Text 
          fontSize="14px" 
          fontWeight={500} 
          color={titleColor}
        >
          {title}
        </Text>
        <Text 
          fontSize="14px" 
          fontFamily={'Roboto, sans-serif !important'} 
          fontWeight={400} 
          color="#ABB7C2"
        >
          {subtitle}
        </Text>
      </Box>
    </Flex>
  );
};
const CreateOfferLetter = () => {

  const defaultSalaryRow = () => ({ name: '', percentage: '', amount: '' });
  const { id } = useParams();
  const [highestTabReached, setHighestTabReached] = useState(id ? 2 : 0);

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const colors = globalStyles.colors;
  const [isOverviewMode, setIsOverviewMode] = useState(false)
  const navigate = useNavigate();

  const isEditMode = Boolean(id);

  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [contentPreview, setContentPreview] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [initialValues, setInitialValues] = useState(
    {
      // Tab 1 fields
      applicationId: '',
      candidateName: '',
      subject: '',

      // Tab 2 fields
      grossSalary: '',
      ctcSalary: '',
      currency: 'INR',
      frequency: 'annually',
      pf: false,
      salaryStructure: [
        { componentName: 'Basic Pay', percentage: 15 },
        { componentName: 'HRA', percentage: 11 },
        { componentName: 'Conveyance Allowance', percentage: 6 },
        { componentName: 'Medical Allowance', percentage: 4 },
        { componentName: 'Special Allowances', percentage: 2 },
        { componentName: 'Gross Earnings (A)', percentage: 2 },
        { componentName: 'EPF Employer Cont. (B)', percentage: 2 },
        { componentName: 'Graduity (D)', percentage: 15 },
        { componentName: 'CTC(A+B+C+D)', percentage: 11 },
        { componentName: 'EPF Deduction (G)', percentage: 6 },
        { componentName: 'Additional Cost', percentage: 4 },
        { componentName: 'Insurance Premium', percentage: 2 },
        { componentName: 'Take home salary (A-G)', percentage: 2 },
        { componentName: 'EPF Employer Cont.', percentage: 2 }
      ],
      // Tab 3 fields
      joiningDate: null,
      offerExpiryDate: null,
      offerLetterContent: '',
    });
  const tabValidationSchemas = useMemo(
    () => ({
      0: Yup.object().shape({
        applicationId: Yup.string().required('Application is required'),
        candidateName: Yup.string().required('Candidate name is required'),
        subject: Yup.string()
          .transform((value) => (value ? value.trim() : '')) // 👈 trims before validating
          .required('Subject is required')
          .min(3, 'Subject must be at least 3 characters')
          .max(100, 'Subject cannot exceed 100 characters')
      }),
      1: Yup.object().shape({
        grossSalary: Yup.number()
          .typeError('Gross salary must be a valid number')
          .required('Gross salary is required')
          .positive('Gross salary must be a positive value')
          .test(
            'max-digits',
            'Gross salary cannot exceed 8 digits before decimal',
            function (value) {
              if (!value && value !== 0) return true;
              const numValue = Number(value);
              if (isNaN(numValue)) return true;
              const str = numValue.toString();
              const parts = str.split('.');
              const integerPart = parts[0].replace('-', ''); // Remove negative sign if any
              return integerPart.length <= 8;
            }
          )
          .test(
            'decimal-places',
            'Gross salary can have maximum 2 decimal places',
            function (value) {
              if (!value && value !== 0) return true;
              const numValue = Number(value);
              if (isNaN(numValue)) return true;
              const str = numValue.toString();
              const parts = str.split('.');
              if (parts.length === 1) return true; // No decimal part
              return parts[1].length <= 2;
            }
          ),
        ctcSalary: Yup.number()
          .typeError('CTC salary must be a valid number')
          .required('CTC salary is required')
          .positive('CTC salary must be a positive value')
          .test(
            'max-digits',
            'CTC salary cannot exceed 8 digits before decimal',
            function (value) {
              if (!value && value !== 0) return true;
              const numValue = Number(value);
              if (isNaN(numValue)) return true;
              const str = numValue.toString();
              const parts = str.split('.');
              const integerPart = parts[0].replace('-', ''); // Remove negative sign if any
              return integerPart.length <= 8;
            }
          )
          .test(
            'decimal-places',
            'CTC salary can have maximum 2 decimal places',
            function (value) {
              if (!value && value !== 0) return true;
              const numValue = Number(value);
              if (isNaN(numValue)) return true;
              const str = numValue.toString();
              const parts = str.split('.');
              if (parts.length === 1) return true; // No decimal part
              return parts[1].length <= 2;
            }
          )
          .test(
            'ctc-greater-than-gross',
            'CTC salary must be greater than or equal to Gross salary',
            function (value) {
              const { grossSalary } = this.parent;
              // Skip validation if value is empty, 0, or not a valid number
              if (!value || value === 0 || isNaN(Number(value))) return true;
              // Skip if grossSalary is not set or is 0
              if (!grossSalary || grossSalary === 0 || isNaN(Number(grossSalary))) return true;
              const ctcNum = Number(value);
              const grossNum = Number(grossSalary);
              // Only validate if both are valid positive numbers
              if (ctcNum <= 0 || grossNum <= 0) return true;
              return ctcNum >= grossNum;
            }
          )
          .test(
            'ctc-greater-than-components',
            'CTC salary must be greater than or equal to sum of salary components',
            function (value) {
              const { salaryStructure } = this.parent;
              if (!value && value !== 0) return true;
              if (!salaryStructure || !Array.isArray(salaryStructure)) return true;
              
              const totalAmount = salaryStructure.reduce(
                (sum, item) => sum + (Number(item.amount) || 0),
                0
              );
              
              if (totalAmount === 0) return true; // Skip if no components entered
              
              return Number(value) >= totalAmount;
            }
          ),
        currency: Yup.string()
          .required('Currency is required'),
        frequency: Yup.string()
          .required('Frequency is required'),
        pf: Yup.boolean(),
        // Optional: for salary breakdown section
        salaryStructure: Yup.array()
          .of(
            Yup.object().shape({
              componentName: Yup.string().required('Component name is required'),
              percentage: Yup.number()
                .min(0, 'Percentage cannot be negative')
                .max(100, 'Percentage cannot exceed 100')
                .required('Percentage is required'),
              amount: Yup.number()
                .typeError('Amount must be a valid number')
                .min(0, 'Amount cannot be negative')
                .required(),
            })
          )
          .min(1, 'At least one salary component is required')
          .test(
            'total-percentage-100',
            'Total percentage must equal 100%',
            function (components) {
              if (!components) return false;

              const total = components.reduce(
                (sum, item) => sum + (Number(item.percentage) || 0),
                0
              );

              return total === 100;
            }
          )

      }),
      2: Yup.object().shape({
        templateId: Yup.string(),

        joiningDate: Yup.date()
          .required('Joining date is required')
          .test(
            'joining-before-expiry',
            'Joining date must be after expiry date',
            function (value) {
              const { offerExpiryDate } = this.parent;

              // ⛔ If expiry date is missing → skip validation → return true
              if (!offerExpiryDate) return true;

              // ⛔ If joining date is missing → fail (because required)
              if (!value) return false;

              // ✅ Validate only when both dates exist
              return new Date(value) < new Date(offerExpiryDate);
            }
          ),


        offerExpiryDate: Yup.date()
          .required('Expiry date is required')
          .test(
            'expiry-after-joining',
            'Expiry date must be before joining date',
            function (value) {
              const { joiningDate } = this.parent;

              // ⛔ If expiry date is missing → skip validation → return true
              if (!joiningDate) return true;

              // ⛔ If joining date is missing → fail (because required)
              if (!value) return false;

              // ✅ Validate only when both dates exist
              return new Date(value) > new Date(joiningDate);
            }
          ),

        offerLetterContent: Yup.string()
          .test(
            "not-empty-trimmed",
            "Offer letter content is required",
            (value) => {
              if (!value) return false;

              const clean = value
                .replace(/<[^>]*>/g, "")   // remove tags
                .replace(/&nbsp;/g, " ")
                .trim();

              return clean.length > 0;
            }
          )
          .required("Offer letter content is required"),

      })
    }),
    []
  );
  const isTabValid = useCallback(
    (values, tabIndex) => {
      try {

        // For other tabs, use schema validation
        tabValidationSchemas[tabIndex].validateSync(values, {
          abortEarly: false,
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    [tabValidationSchemas, id,]
  );

  const getTabStates = useCallback(
    (values, dirty, isValid, currentTabIndex) => {
      const tabStates = {};
  
      // Find the first invalid tab from the beginning
      let firstInvalidTab = null;
      for (let i = 0; i <= highestTabReached; i++) {
        if (!isTabValid(values, i)) {
          firstInvalidTab = i;
          break;
        }
      }
  
      for (let i = 0; i <= 2; i++) {
        // PAST tabs (before current)
        if (i < currentTabIndex) {
          const tabValid = isTabValid(values, i);
          tabStates[i] = {
            isAccessible: true,
            isValid: tabValid,        // Will show tick if valid
            isPast: true,              // Mark as past tab
            canProceed: tabValid
          };
        } 
        // CURRENT tab
        else if (i === currentTabIndex) {
          const currentTabValid = isTabValid(values, i);
          tabStates[i] = {
            isAccessible: true,
            isValid: currentTabValid,  // For internal checks, but won't show tick
            isPast: false,             // Not a past tab
            canProceed: currentTabValid,
          };
        } 
        // FUTURE tabs (after current)
        else {
          // If there's an invalid tab before this one, lock it
          if (firstInvalidTab !== null && i > firstInvalidTab) {
            tabStates[i] = {
              isAccessible: false,
              isValid: false,
              isPast: false,
              canProceed: false,
            };
          } 
          // If this tab was previously reached and all previous tabs are valid, keep it accessible
          else if (i <= highestTabReached && firstInvalidTab === null) {
            tabStates[i] = {
              isAccessible: true,
              isValid: false,          // Don't show tick for future tabs
              isPast: false,           // Not a past tab
              canProceed: false,
            };
          } 
          // Next immediate tab is always accessible if all previous are valid
          else if (i === currentTabIndex + 1 && firstInvalidTab === null) {
            tabStates[i] = {
              isAccessible: true,
              isValid: false,          // Don't show tick
              isPast: false,
              canProceed: false,
            };
          } 
          // All other future tabs that haven't been reached - locked
          else {
            tabStates[i] = {
              isAccessible: false,
              isValid: false,
              isPast: false,
              canProceed: false,
            };
          }
        }
      }
      console.log(tabStates,'tabStates');
      
      return tabStates;
    },
    [isTabValid, highestTabReached]
  );
  const handleTabChange = useCallback(
    (index, values, dirty, isValid) => {
      if (index < activeTabIndex) {
        setActiveTabIndex(index);
        return;
      }

      if (index === activeTabIndex) {
        return;
      }

      const tabStates = getTabStates(values, dirty, isValid, activeTabIndex);

      if (tabStates[index]?.isAccessible) {
        setActiveTabIndex(index);
      } else {
        toast.error("Please complete the current step before proceeding");
      }
    },
    [activeTabIndex, getTabStates]
  );
  const handleNext = useCallback(
    (values, dirty, isValid, setErrors, setTouched) => {
      const currentTabValid = isTabValid(values, activeTabIndex);

      if (currentTabValid) {
        const newTabIndex = activeTabIndex + 1;
        setActiveTabIndex(newTabIndex);
        if (newTabIndex > highestTabReached) {
          setHighestTabReached(newTabIndex);
        }
      } else {
        try {
          tabValidationSchemas[activeTabIndex].validateSync(values, {
            abortEarly: false,
          });
        } catch (error) {
          const errors = {};
          const touchedFields = {};

          error.inner.forEach((err) => {
            errors[err.path] = err.message;
            touchedFields[err.path] = true;
          });

          setErrors(errors);
          setTouched(touchedFields);
          toast.error("Please fill all required fields before proceeding");
        }
      }
    },
    [activeTabIndex, isTabValid, tabValidationSchemas, highestTabReached]
  );

  const handleBack = useCallback(() => {
    if (activeTabIndex > 0) {
      setActiveTabIndex(activeTabIndex - 1);
    }
  }, [activeTabIndex]);

  // Helper function to format salary input (max 8 digits before decimal, max 2 decimal places)
  const handleSalaryInputChange = (value, field, setFieldValue, setFieldTouched, validateField) => {
    // Remove any non-numeric characters except decimal point
    let cleaned = value.replace(/[^\d.]/g, '');
    
    // Prevent negative values
    if (cleaned.startsWith('-')) {
      cleaned = cleaned.replace('-', '');
    }
    
    // Prevent multiple decimal points
    const decimalIndex = cleaned.indexOf('.');
    if (decimalIndex !== -1) {
      cleaned = cleaned.substring(0, decimalIndex + 1) + cleaned.substring(decimalIndex + 1).replace(/\./g, '');
    }
    
    // Split by decimal point
    const parts = cleaned.split('.');
    
    // Remove leading zeros from integer part (but allow single "0")
    if (parts[0].length > 1 && parts[0].startsWith('0') && !parts[0].startsWith('0.')) {
      parts[0] = parts[0].replace(/^0+/, '') || '0';
    }
    
    // Limit integer part to 8 digits
    if (parts[0].length > 8) {
      parts[0] = parts[0].substring(0, 8);
    }
    
    // Limit decimal part to 2 digits
    if (parts.length > 1 && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }
    
    // Reconstruct the value
    const formattedValue = parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
    
    // Update the field value (empty string if invalid)
    if (formattedValue === '' || formattedValue === '.') {
      setFieldValue(field, '');
    } else {
      // Convert to number for Yup validation, but keep as string for display
      const numValue = parseFloat(formattedValue);
      if (!isNaN(numValue)) {
        setFieldValue(field, numValue);
      } else {
        setFieldValue(field, '');
      }
    }
    
    // Mark field as touched
    setFieldTouched(field, true, false);
    
    // Trigger validation for cross-field dependencies
    if (field === 'grossSalary') {
      // When grossSalary changes, re-validate ctcSalary
      setTimeout(() => {
        validateField('ctcSalary');
      }, 100);
    } else if (field === 'ctcSalary') {
      // When ctcSalary changes, validate itself (it depends on grossSalary)
      setTimeout(() => {
        validateField('ctcSalary');
      }, 100);
    }
  };
  useEffect(() => {
    fetchApplications();
    if (isEditMode) fetchOfferLetter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchApplications = async () => {
    try {
      setLoadingApplications(true);
      // Fetch only applications with approved document verification AND accepted LOI
      const response = await getApi('api/recruitment/application/eligible-for-offer');

      let applicationsData = [];
      if (Array.isArray(response)) {
        applicationsData = response;
      } else if (Array.isArray(response.data)) {
        applicationsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        applicationsData = response.data.data;
      }
      setApplications(applicationsData);
    } catch (error) {
      toast.error('Failed to load applications');
      console.error('Error fetching applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };
  useEffect(() => {
    const mode = sessionStorage.getItem("mode");
    if (mode === "overview") {
      setIsOverviewMode(true);
    } else {
      setIsOverviewMode(false);
    }
  }, []);
  const fetchOfferLetter = async () => {
    try {
      const response = await offerLetterService.getOfferLetter(id);
      const offer = response.data;
      const val = {
        applicationId: offer?.application?._id || '',
        candidateName: offer?.candidate?.name || '',
        subject: offer?.subject || '',
        offerLetterContent: offer?.content || '',
        templateId: isEditMode || isOverviewMode ? offer?.templateUsed?._id
          : offer?.templateUsed?._id || '',
        pf: offer?.pf || false,
        grossSalary: offer?.salary?.amount || '',
        ctcSalary: offer?.ctcSalary || '',
        currency: offer?.salary?.currency || 'INR',
        frequency: offer?.salary?.frequency || 'annually',
        salaryStructure: Array.isArray(offer?.salaryStructure) && offer?.salaryStructure.length
          ? offer?.salaryStructure.map((s) => ({
            componentName: s.component || '',
            percentage: s.percentage || '',
            amount: s.amount || '',
          }))
          : [defaultSalaryRow()],
        joiningDate: offer?.joiningDate ? new Date(offer?.joiningDate) : '',
        offerExpiryDate: offer?.offerExpiryDate ? new Date(offer?.offerExpiryDate) : ''
      }

      setInitialValues(val);

      setContentPreview(offer.contentPreview || offer.content || '');
    } catch (error) {
      toast.error('Failed to load offer letter');
      console.error(error);
    }
  };


  const handleApplicationChange = (
    applicationId,
    setFieldValue,
    setFieldTouched,
    setFieldError
  ) => {
    const application = applications.find((app) => app._id === applicationId);

    if (application) {
      const job = application.job;
      // Try multiple sources for candidate name
      const candidateName = application?.candidateDetails?.name || application?.candidate?.name || '';
      setFieldValue('candidateName', candidateName);

      // Auto-populate subject with "Offer Letter - {candidateName}"
      if (candidateName) {
        setFieldValue('subject', `Offer Letter - ${candidateName}`);
      } else {
        setFieldValue('subject', '');
      }

      // Clear both field errors when a valid selection is made
      setFieldTouched('applicationId', false);
      setFieldTouched('candidateName', false);
      setFieldError('applicationId', undefined);
      setFieldError('candidateName', undefined);

      const joiningDate = new Date();
      joiningDate.setDate(joiningDate.getDate() + 30);
      // setFieldValue('joiningDate', joiningDate);

      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);
      // setFieldValue('offerExpiryDate', expiryDate);

      if (job?.salary) setFieldValue('salary.amount', job.salary);
    } else {
      // ✅ Reset and show validation error
      setFieldValue('candidateName', '');
      setFieldValue('subject', ''); // Clear subject if application is cleared

      // Mark the field as touched AND trigger validation
      setFieldTouched('candidateName', true, true);

      // Optional: set custom error message instantly
      setFieldError('candidateName', 'Candidate name is required');
    }
  };
  const [permission] = HasAccess([accessModuleTitles.Offers]);

  const calcBreakdownTotals = (salaryStructure) => {
    const totalPercent = (salaryStructure || []).reduce((acc, it) => acc + Number(it.percentage || 0), 0);
    const totalAmount = (salaryStructure || []).reduce((acc, it) => acc + Number(it.amount || 0), 0);
    return { totalPercent, totalAmount };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { offerLetterContent, grossSalary, currency, frequency, ctcSalary, salaryStructure, ...rest } = values
      const payload = {
        ...rest,
        salaryStructure: salaryStructure.map((item) => {
          const { componentName, ...restEl } = item
          return ({ ...restEl, component: componentName })
        }),
        salary
          : {
          amount: values.grossSalary,
          currency: values.currency,
          frequency: values.frequency
        },
        ctcSalary: ctcSalary,
        content: offerLetterContent,
        templateId: rest.templateId || undefined,
      };
      console.log(payload, 'payload32');


      if (isEditMode) {
        await offerLetterService.updateOfferLetter(id, payload);
        toast.success('Offer letter updated successfully');
      } else {
        await offerLetterService.createOfferLetter(payload);
        toast.success('Offer letter created successfully');
      }
      navigate('/recruitment/offers');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update offer letter' : 'Failed to create offer letter');
      console.error(error);
    } finally {
      // setSubmitting(false);
    }
  };
  console.log(isOverviewMode, 'isOverviewMode');

  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [originalOfferData, setOriginalOfferData] = useState(null);
  return (
    <Stack spacing={6} p={5} className='offer-letter'>
      <Box className="createJobOpening-mainContainer">
        <Box className="createJobOpening-breadCrumb">
          <ArrowBackIcon
            onClick={() => navigate(-1)}
            fontSize="20px"
            cursor="pointer"
          />
          <Text className="createJobOpening-pageTitle">
            {isOverviewMode ? "Overview" : id ? recruitConstant.editOfferLetter : recruitConstant.addOfferLetter}
          </Text>
        </Box>
      </Box>

      <Box bg="white" borderRadius="md" shadow="sm">
        <>
          <Box className="createJobOpening-container">


            <Formik
              enableReinitialize
              initialValues={
                initialValues
              }
              validationSchema={offerValidationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                setFieldValue,
                handleSubmit,
                errors,
                dirty,
                isValid,
                setErrors,
                touched,
                setFieldError,
                setFieldTouched,
                setTouched,
              }) => {
                const tabStates = getTabStates(
                  values,
                  dirty,
                  isValid,
                  activeTabIndex
                );
                const reStructuredVal = values;
                console.log(errors, values, 'errors34532');

                const {

                  ...baseValues
                } = reStructuredVal;

                let finalValues = { ...baseValues };

                const isDirty = isFormDirty(
                  originalOfferData || OFFER_INITIAL_VALUES,
                  finalValues
                );
                console.log(
                  addLoading,
                  editLoading,
                  (!id && !dirty),
                  !isValid,
                  !permission?.update
                  , (activeTabIndex === 2 && !isEditMode)
                  , (isEditMode && !isDirty),
                  errors, values,
                  'addLoading---------');


                return (
                  <Form onSubmit={handleSubmit}>
                    <Tabs
                      index={activeTabIndex}
                      onChange={(index) =>
                        handleTabChange(index, values, dirty, isValid)
                      }
                      // className="createJobOpening-tabsContainer"
                      variant="unstyled"
                      orientation="horizontal"
                    >
                      < HStack w={'100%'} spacing={8} pt={6} pl={6} pr={6} mb={6} gap={'21px'} alignItems={'start'}
                      >
                        {offerTabs.map(({ title, subtitle, tab }, index) => {
                          const tabState = tabStates[index];
                          const isCurrentTab = index === activeTabIndex;

                          return (
                            <Flex key={index}>
                              <Step
                                index={tab}
                                title={title}
                                isCurrentTab={isCurrentTab}
                                subtitle={subtitle}
                                active={index + 1 == tab}
                                completed={false}
                                tabState={tabState}
                                onClick={() => {
                                  console.log(index, 'isCurrentTab');

                                  setActiveTabIndex(index);
                                }}
                              />
                              {index !== offerTabs.length - 1 && <Image src={Arrow} h={'8px'} mt={'8px'} />}

                            </Flex>

                          );
                        })}
                      </HStack>

                      <TabPanels
                        w={"100%"}
                        className="createJobOpening-tabsPanelContainer"
                      >
                        {/* Personal Information tab - KEEPING ALL EXISTING CONTENT */}
                        <TabPanel className="createJobOpening-tabsPanel">
                          {/* ... All existing personal info fields remain exactly the same ... */}
                          <VStack gap={0} align="stretch"  >
                            <Box px={6} pt={4}>
                              <Alert status="info" borderRadius="md" fontSize="sm">
                                <AlertIcon />
                                Only applications with approved document verification and accepted LOI are shown.
                              </Alert>
                            </Box>
                            <Flex gap={"20px"} p={6}>
                              <Box className='field-container' w={"50%"}>
                                <FormLabel fontWeight={'400'} fontSize={'14px'} fontFamily="Roboto, sans-serif">
                                  {recruitConstant.selectApplication}<span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Field name="applicationId">
                                  {({ field, meta }) => {
                                    console.log(field.value, applications, 'applications');

                                    return (
                                      <>
                                        <Select
                                          {...field}
                                          disabled={!permission?.update || isOverviewMode}
                                          borderRadius={8}
                                          fontFamily="Roboto, sans-serif"
                                          placeholder={loadingApplications ? recruitConstant.loading : recruitConstant.selectApplicationPlaceholder}
                                          onChange={(e) => {
                                            field.onChange(e);
                                            handleApplicationChange(e.target.value, setFieldValue, setFieldTouched, setFieldError);
                                          }}
                                          borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        >
                                          {applications
                                            .filter((app) => 
                                              app?.job?.deleted !== true && 
                                              app?.candidateDetails?.deleted !== true
                                            )
                                            .map((app) => {
                                              const candidateName = app?.candidateDetails?.name || app?.candidate?.name || '';
                                              const jobTitle = app?.job?.postingTitle || '';
                                              const appId = app?.customId || '';
                                              
                                              // Format: "Amit Roy - senior-software-engineer (APPID-114)"
                                              let displayText = '';
                                              if (candidateName && jobTitle && appId) {
                                                displayText = `${candidateName} - ${jobTitle} (APPID-${appId})`;
                                              } else if (candidateName && jobTitle) {
                                                displayText = `${candidateName} - ${jobTitle}`;
                                              } else if (jobTitle && appId) {
                                                displayText = `${jobTitle} (APPID-${appId})`;
                                              } else {
                                                displayText = jobTitle || appId || 'Application';
                                              }

                                              return (
                                                <option key={app._id} value={app._id}>
                                                  {displayText}
                                                </option>
                                              );
                                            })}
                                        </Select>
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText} fontSize="sm" mt={1}>{meta.error}</Text>
                                        )}
                                      </>
                                    )
                                  }}
                                </Field>
                              </Box>

                              <Box className='field-container' w={"50%"}>
                                <FormLabel fontWeight={'400'} fontSize={'14px'} fontFamily="Roboto, sans-serif">
                                  {recruitConstant.candidateName} <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Field name="candidateName">
                                  {({ field, meta }) => (
                                    <>
                                      <Input
                                        {...field}
                                        disabled
                                        borderRadius={8}
                                        fontFamily="Roboto, sans-serif"
                                        placeholder={recruitConstant.candidatePlaceholder}
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                      />
                                      {meta.touched && meta.error && (
                                        <Text color={colors.errorText} fontSize="sm" mt={1}>{meta.error}</Text>
                                      )}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>

                            <Flex gap={"20px"} p={6} pt={0}>
                              <Box className='field-container' w={"50%"}>
                                <FormLabel fontWeight={'400'} fontSize={'14px'} fontFamily="Roboto, sans-serif">
                                  {recruitConstant.emailSubject}<span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Field name="subject">
                                  {({ field, meta }) => (
                                    <>
                                      <Input
                                        {...field}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (!value.startsWith(' ')) {
                                            field.onChange(e);
                                          }
                                        }}
                                        borderRadius={8}
                                        disabled={!permission?.update || isOverviewMode}
                                        fontFamily="Roboto, sans-serif"
                                        placeholder={recruitConstant.emailSubjectPlaceholder}
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                      />
                                      {meta.touched && meta.error && (
                                        <Text color={colors.errorText} fontSize="sm" mt={1}>{meta.error}</Text>
                                      )}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>
                            <Box width="100%" height="1px" bg="#CFD6DC" />
                            <Flex justify="flex-end" p={6} gap={'16px'}>
                              <Button className="theme-btn2 cancelBtn"
                                sx={{
                                  textTransform: "capitalize",
                                }}
                                variant="outline"
                                colorScheme={colors.error["100"]}
                                size="sm" onClick={() => navigate("/recruitment/offers")}>
                                {constant.cancel}
                              </Button>
                              <Button
                                disabled={!tabStates[activeTabIndex]?.canProceed}
                                className="nextBtn"
                                variant="primary"
                                onClick={() =>
                                  handleNext(
                                    values,
                                    dirty,
                                    isValid,
                                    setErrors,
                                    setTouched
                                  )
                                }
                              >
                                {recruitConstant.next}
                              </Button>
                            </Flex>
                          </VStack>
                        </TabPanel>

                        <TabPanel className="createJobOpening-tabsPanel">
                          <VStack spacing={4} gap={0} align="stretch" >
                            <VStack gap={0} align="stretch" border="1px solid" m={4}
                              borderColor="#CFD6DC"
                              borderRadius="8px">
                              <Text p={4} pb={3} fontSize="16px" fontWeight="500">Compensation Package</Text>

                              <Flex gap={"16px"} p={4} pt={0} flexWrap="wrap">
                                <Box className='field-container' flex="1" minW="200px">
                                  <FormLabel fontWeight={'400'} fontSize={'14px'} fontFamily="Roboto, sans-serif" mb={1}>
                                    {recruitConstant.grossSalary}<span style={{ color: 'red' }}>*</span>
                                  </FormLabel>
                                  <Field name="grossSalary">
                                    {({ field, meta, form }) => (
                                      <>
                                        <Input
                                          disabled={!permission?.update || isOverviewMode}
                                          type="text"
                                          inputMode="decimal"
                                          borderRadius={8}
                                          fontFamily="Roboto, sans-serif"
                                          placeholder="0"
                                          size="sm"
                                          value={field.value || ''}
                                          borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                          onKeyDown={(e) => {
                                            if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                              e.preventDefault();
                                            }
                                          }}
                                          onChange={(e) => {
                                            handleSalaryInputChange(
                                              e.target.value, 
                                              'grossSalary', 
                                              form.setFieldValue,
                                              form.setFieldTouched,
                                              form.validateField
                                            );
                                          }}
                                          onBlur={(e) => {
                                            field.onBlur(e);
                                            form.setFieldTouched('grossSalary', true, false);
                                            // Re-validate ctcSalary when grossSalary changes
                                            form.validateField('ctcSalary');
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText} fontSize="xs" mt={0.5}>{meta.error}</Text>
                                        )}
                                        {field.value && !meta.error && (
                                          <Flex align="center" gap={1} mt={1}>
                                            <InfoIcon color="gray.500" fontSize="11px" />
                                            <Text color="gray.600" fontSize="xs">
                                              CTC should be ≥ Gross Salary
                                            </Text>
                                          </Flex>
                                        )}
                                      </>
                                    )}
                                  </Field>
                                </Box>
                                <Box className='field-container' flex="1" minW="200px">
                                  <FormLabel fontWeight={'400'} fontSize={'14px'} fontFamily="Roboto, sans-serif" mb={1}>
                                    {recruitConstant.ctcSalary} <span style={{ color: 'red' }}>*</span>
                                  </FormLabel>
                                  <Field name="ctcSalary">
                                    {({ field, meta, form }) => {
                                      const grossSalary = form.values.grossSalary;
                                      const ctcSalary = field.value;
                                      const grossNum = Number(grossSalary) || 0;
                                      const ctcNum = Number(ctcSalary) || 0;

                                      // Determine if field has meaningful value (not empty, not just 0)
                                      const hasValue = ctcSalary !== '' && ctcSalary !== null && ctcSalary !== undefined && ctcNum > 0;
                                      const hasGrossValue = grossNum > 0;

                                      // Only show error if touched and has actual error message and field has a value
                                      const shouldShowError = meta.touched && meta.error && hasValue;

                                      // Show comparison warning only when both fields have valid positive numbers
                                      const shouldShowWarning = hasValue && hasGrossValue && ctcNum < grossNum && !meta.error;

                                      // Show helper text only when: gross has value, ctc is empty, and field not touched yet
                                      const shouldShowHelper = hasGrossValue && !hasValue && !meta.touched;

                                      return (
                                        <>
                                          <Input
                                            disabled={!permission?.update || isOverviewMode}
                                            type="text"
                                            inputMode="decimal"
                                            borderRadius={8}
                                            fontFamily="Roboto, sans-serif"
                                            placeholder="0"
                                            size="sm"
                                            value={field.value || ''}
                                            borderColor={shouldShowError ? colors.errorText : colors.borderColor}
                                            onKeyDown={(e) => {
                                              if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                                e.preventDefault();
                                              }
                                            }}
                                            onChange={(e) => {
                                              handleSalaryInputChange(
                                                e.target.value,
                                                'ctcSalary',
                                                form.setFieldValue,
                                                form.setFieldTouched,
                                                form.validateField
                                              );
                                            }}
                                            onBlur={(e) => {
                                              field.onBlur(e);
                                              form.setFieldTouched('ctcSalary', true, false);
                                              form.validateField('ctcSalary');
                                            }}
                                          />

                                          {/* Only show error when there's a real validation error */}
                                          {shouldShowError && (
                                            <Text color={colors.errorText} fontSize="xs" mt={0.5}>
                                              {meta.error}
                                            </Text>
                                          )}

                                          {/* Show warning when CTC is less than Gross */}
                                          {shouldShowWarning && (
                                            <Flex align="center" gap={1} mt={1}>
                                              <InfoIcon color="orange.500" fontSize="12px" />
                                              <Text color="orange.600" fontSize="xs">
                                                CTC ({ctcNum.toLocaleString('en-IN')}) should be greater than or equal to Gross Salary ({grossNum.toLocaleString('en-IN')})
                                              </Text>
                                            </Flex>
                                          )}

                                          {/* Show helper text when CTC is empty and Gross has value */}
                                          {shouldShowHelper && (
                                            <Flex align="center" gap={1} mt={1}>
                                              <InfoIcon color="blue.500" fontSize="12px" />
                                              <Text color="blue.600" fontSize="xs">
                                                CTC should be greater than or equal to Gross Salary ({grossNum.toLocaleString('en-IN')})
                                              </Text>
                                            </Flex>
                                          )}
                                        </>
                                      );
                                    }}
                                  </Field>
                                </Box>
                                <Box className='field-container' flex="1" minW="150px">
                                  <FormLabel fontWeight={'400'} fontSize={'14px'} fontFamily="Roboto, sans-serif" mb={1}>
                                    {recruitConstant.currency}<span style={{ color: 'red' }}>*</span>
                                  </FormLabel>
                                  <Field name="currency">
                                    {({ field, meta }) => (
                                      <>
                                        <Select
                                          {...field}
                                          borderRadius={8}
                                          disabled={!permission?.update || isOverviewMode}
                                          fontFamily="Roboto, sans-serif"
                                          placeholder="Select currency"
                                          size="sm"
                                          borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        >
                                          {currencyOptions.map((currency) => (
                                            <option key={currency.value} value={currency.value}>
                                              {currency.label}
                                            </option>
                                          ))}
                                        </Select>
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText} fontSize="xs" mt={0.5}>{meta.error}</Text>
                                        )}
                                      </>
                                    )}
                                  </Field>
                                </Box>
                                <Box className='field-container' flex="1" minW="150px">
                                  <FormLabel fontWeight={'400'} fontSize={'14px'} fontFamily="Roboto, sans-serif" mb={1}>
                                    {recruitConstant.Frequency}<span style={{ color: 'red' }}>*</span>
                                  </FormLabel>
                                  <Field name="frequency">
                                    {({ field, meta }) => (
                                      <>
                                        <Select
                                          {...field}
                                          borderRadius={8}
                                          disabled={!permission?.update || isOverviewMode}
                                          fontFamily="Roboto, sans-serif"
                                          placeholder="Select frequency"
                                          size="sm"
                                          borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        >
                                          {salaryFrequencyOptions.map((freq) => (
                                            <option key={freq.value} value={freq.value}>
                                              {freq.label}
                                            </option>
                                          ))}
                                        </Select>
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText} fontSize="xs" mt={0.5}>{meta.error}</Text>
                                        )}
                                      </>
                                    )}
                                  </Field>
                                </Box>
                              </Flex>
                              <Flex gap={"12px"} p={4} pt={2} className='pf-container'>
                                <Field name="pf" type="checkbox">
                                  {({ field }) => {
                                    return (
                                      <Checkbox
                                        {...field}
                                        disabled={!permission?.update || isOverviewMode}
                                        isChecked={field.value}
                                        fontFamily="Roboto, sans-serif"
                                        size="sm"
                                      >
                                        <Text fontSize="14px" fontWeight="400">
                                          {recruitConstant.pfRequired}
                                        </Text>
                                      </Checkbox>
                                    )
                                  }}
                                </Field>
                              </Flex>
                            </VStack>
                            <Box p={4} pt={0}>
                              <Field name="salaryStructure">
                                {({ field, meta, form }) => {
                                  console.log(field.value, 'field34532');

                                  return (
                                    <>
                                      <SalaryStructure
                                        permission={permission}
                                        isOverviewMode={isOverviewMode}
                                        totalSalary={values.grossSalary}
                                        value={field.value}
                                        errors={form.errors.salaryStructure}
                                        touched={form.touched}
                                        setFieldValue={form.setFieldValue}
                                        setFieldTouched={form.setFieldTouched}   // 👈 important
                                      />

                                    </>
                                  )
                                }}
                              </Field>


                            </Box>
                            <Box width="100%" height="1px" bg="#CFD6DC" />
                            <Flex justify="flex-end" gap={'16px'} p={6} >
                              <Button onClick={handleBack} className="backBtn">
                                {constant.back}
                              </Button>
                              <Button
                                disabled={!tabStates[activeTabIndex]?.canProceed}
                                className="nextBtn"
                                variant="primary"
                                onClick={() =>
                                  handleNext(
                                    values,
                                    dirty,
                                    isValid,
                                    setErrors,
                                    setTouched
                                  )
                                }
                              >Next</Button>
                            </Flex>
                          </VStack>


                        </TabPanel>
                        <TabPanel p={0}>
                          <VStack spacing={6} align="stretch">
                            <Text fontSize="16px" fontWeight="500" pl={5} pr={5} pt={5}>Important Date</Text>
                            <VStack align="stretch" border="1px solid" ml={6} mr={6} p={5} gap={5}
                              borderColor="#CFD6DC"
                              borderRadius="8px">
                              <Flex gap={4}>
                                <Box flex="1">
                                  <FormLabel>
                                    Joining Date <span style={{ color: 'red' }}>*</span>
                                  </FormLabel>
                                  <Field name="joiningDate">
                                    {({ field, meta, form }) => {
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      
                                      return (
                                        <>
                                          <CustomDatePicker
                                            isDisabled={!permission?.update || isOverviewMode}
                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                            fullWidth={true}
                                            startDate={field.value}
                                            placeholder={'dd-mm-yyyy'}
                                            minDate={today}
                                            setStartDate={(date) => form.setFieldValue(field.name, date)}
                                          />
                                          {meta.touched && meta.error && (
                                            <Text color={colors.errorText} fontSize="sm" mt={1}>{meta.error}</Text>
                                          )}
                                        </>
                                      );
                                    }}
                                  </Field>
                                </Box>

                                <Box flex="1">
                                  <FormLabel>
                                    Offer Expiry Date <span style={{ color: 'red' }}>*</span>
                                  </FormLabel>
                                  <Field name="offerExpiryDate">
                                    {({ field, meta, form }) => {
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      const joiningDate = form.values.joiningDate;
                                      
                                      let maxDate = undefined;
                                      if (joiningDate) {
                                        maxDate = new Date(joiningDate);
                                        maxDate.setDate(maxDate.getDate() - 1);
                                      }
                                      
                                      return (
                                        <>
                                          <CustomDatePicker
                                            isDisabled={!permission?.update || isOverviewMode}
                                            onBlur={() => form.setFieldTouched(field.name, true)}
                                            fullWidth={true}
                                            placeholder={'dd-mm-yyyy'}
                                            startDate={field.value}
                                            minDate={today}
                                            maxDate={maxDate}
                                            setStartDate={(date) => form.setFieldValue(field.name, date)}
                                          />
                                          {meta.touched && meta.error && (
                                            <Text color={colors.errorText} fontSize="sm" mt={1}>{meta.error}</Text>
                                          )}
                                        </>
                                      );
                                    }}
                                  </Field>
                                </Box>
                              </Flex>
                            </VStack>

                            <Box>
                              <Field name="offerLetterContent">
                                {({ field, meta }) => (
                                  <>
                                    <OfferContentEditor
                                      isOffer={true}
                                      value={field.value}
                                      permission={permission}
                                      isOverviewMode={isOverviewMode}
                                      values={values}
                                      setContentPreview={setContentPreview}
                                      onChange={(v) => setFieldValue('offerLetterContent', v)}
                                      previewContent={contentPreview}
                                      isEditMode={isEditMode}
                                      setFieldValue={setFieldValue}
                                    />
                                    {meta.error && typeof meta.error === "string" && (
                                      <Text
                                        m={6}
                                        mb={0}
                                        fontWeight="400!important"
                                        fontSize="14px"
                                        borderRadius={'4px'}
                                        // mt={2}
                                        bg={'#f7b8b8'}
                                        color={'#000'}
                                        p={'8px'}
                                        textAlign={'left'}
                                        className="roboto"  >
                                        {meta.error}
                                      </Text>
                                    )}
                                  </>
                                )}
                              </Field>
                            </Box>
                            {/* </VStack> */}

                            <Box width="100%" height="1px" bg="#CFD6DC" />
                            <Flex justify="flex-end" gap={'16px'} p={6} pt={0} >
                              <Button onClick={handleBack} className="backBtn">
                                {constant.back}
                              </Button>
                              <Button onClick={() => { }} className="backBtn">Save as Draft</Button>
                              {
                                isOverviewMode ? <Button
                                  onClick={() => navigate("/recruitment/offers")}
                                  className="nextBtn"
                                  variant="primary"
                                  type="button"
                                >
                                  Return to Offers
                                </Button> : <Button
                                  disabled={
                                    addLoading ||
                                    editLoading ||
                                    (!id && !dirty) ||
                                    !isValid ||
                                    !permission?.update
                                    || (isEditMode && !isDirty)
                                  }
                                  className="nextBtn"
                                  variant="primary"
                                  type="submit"
                                >{addLoading || editLoading ? (
                                  <Spinner size="sm" />
                                ) : id ? 'Update Offer Letter' : 'Create Offer Letter'
                                  }
                                </Button>
                              }
                            </Flex>

                          </VStack>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>

                  </Form>
                );
              }}
            </Formik >

          </Box >
        </>
      </Box>
    </Stack>
  );
};

export default CreateOfferLetter;
